import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Loader2, ExternalLink, ChevronDown, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import useSubscription from '@/hooks/useSubscription';
import LoadingSpinner from '@/components/ui/loading-spinner';
import SEO from '@/components/seo';

const PALETTE = {
  inchworm: '#B1FA63',
  gunmetal: '#243837',
  orange: '#FE7733',
  paleViolet: '#B2A1FF',
  brightSnow: '#FFFFFF',
  americanSilver: '#D1D1D1',
};

const SubscriptionPage: React.FC = () => {
  const navigate = useNavigate();
  const { subscription, plans, isLoading, createCheckoutSession, createBillingPortalSession } = useSubscription();
  const [processingPlan, setProcessingPlan] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [openFaqItems, setOpenFaqItems] = useState<boolean[]>([]);

  const toggleFaqItem = (index: number) => {
    setOpenFaqItems((prev) => {
      const next = [...prev];
      next[index] = !next[index];
      return next;
    });
  };

  const handleSubscribe = async (planId: string = 'starter') => {
    setProcessingPlan(planId);
    setError(null);
    
    try {
      const data = await createCheckoutSession(planId);
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start checkout');
      setProcessingPlan(null);
    }
  };

  const handleManageSubscription = async () => {
    setProcessingPlan('billing');
    setError(null);
    
    try {
      const data = await createBillingPortalSession();
      if (data.portal_url) {
        window.location.href = data.portal_url;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to open billing portal');
      setProcessingPlan(null);
    }
  };

  if (isLoading) {
    return <LoadingSpinner size="lg" fullPage />;
  }

  const starterPlan = plans.find(p => p.id === 'starter') || {
    id: 'starter',
    name: 'Starter',
    monthly_price: 29,
    monthly_video_limit: 30
  };

  const proPlan = plans.find(p => p.id === 'pro') || {
    id: 'pro',
    name: 'Pro',
    monthly_price: 69,
    monthly_video_limit: 100
  };

  const hasActiveSubscription = subscription?.has_subscription && subscription?.status === 'active';
  const isTrialing = subscription?.has_subscription && subscription?.status === 'trialing';
  const isTrialEnded = subscription?.has_subscription && subscription?.status === 'paused';

  const faqItems = [
    {
      question: "What counts as a video check?",
      answer:
        "A video check is counted each time a creator uploads a video and BrandComply checks it against your campaign brief. If a creator re-uploads a video, that upload is checked again and counts as an additional video check. The dashboard shows only videos that are currently pending or have been finally submitted, so the number of video checks used can be higher than the number of videos visible in the dashboard.",
    },
    {
      question: "What happens when I reach my limit?",
      answer:
        "Once you've used all your video checks for the month, new video submissions will be paused until your next billing cycle begins or you upgrade your plan.",
    },
    {
      question: "Can I cancel my plan anytime?",
      answer:
        "Yes! You can cancel your plan at any time through the billing portal. You'll continue to have access until the end of your current billing period.",
    },
  ];

  return (
    <div className="min-h-screen" style={{ background: `linear-gradient(135deg, ${PALETTE.brightSnow} 0%, #f5f5f5 100%)` }}>
      <SEO
        title="Subscription Plans"
        description="Choose the right BrandComply plan for your team. Flexible pricing for influencer and UGC video compliance at any scale."
        path="/subscription"
      />
      {/* Header - match public campaign result style (logo top-left) */}
      <div className="border-b" style={{ borderColor: `${PALETTE.americanSilver}40` }}>
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/brandcomply_logo_gunmetal.png" alt="Brand Logo" className="h-6 w-auto" />
            <span className="text-xl font-bold" style={{ color: PALETTE.gunmetal }}>
              BrandComply
            </span>
          </div>
          <button
            onClick={() => navigate('/brand/campaign')}
            className="flex items-center gap-2 text-sm font-medium hover:opacity-70 transition-opacity"
            style={{ color: PALETTE.gunmetal }}
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Title Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4" style={{ color: PALETTE.gunmetal }}>
            {hasActiveSubscription ? 'Your Plan' : isTrialEnded ? 'Your free trial has ended' : 'Automate Your IGC & UGC Video Reviews'}
          </h1>
          {isTrialEnded && (
            <p className="text-lg" style={{ color: `${PALETTE.gunmetal}80` }}>
              Choose a plan to keep checking creator videos against your briefs.
            </p>
          )}
          {isTrialing && (
            <div className="inline-flex flex-col items-center gap-2 mt-2 px-6 py-4 rounded-xl" style={{ background: `${PALETTE.inchworm}10`, border: `1px solid ${PALETTE.inchworm}30` }}>
              <p className="text-lg font-medium" style={{ color: PALETTE.gunmetal }}>
                You're on a <strong>14-day free trial</strong> — {(subscription as any)?.trial_days_remaining ?? 0} days remaining
              </p>
              <p className="text-sm" style={{ color: `${PALETTE.gunmetal}70` }}>
                30 video checks included · No credit card required · Subscribe anytime to continue after your trial
              </p>
            </div>
          )}
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-6 max-w-7xl mx-auto items-stretch">
          {/* Starter Plan Card */}
          <Card className="w-full border-2 hover:border-opacity-100 transition-all duration-300 flex flex-col" 
            style={{ borderColor: `${PALETTE.americanSilver}60` }}>
            <CardContent className="p-8 flex flex-col flex-1">
              {/* Plan Header */}
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold mb-1" style={{ color: PALETTE.gunmetal }}>
                  {starterPlan.name}
                </h2>                
              </div>

              {/* Price */}
              <div className="text-center mb-6">
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-5xl font-bold" style={{ color: PALETTE.gunmetal }}>
                    €{starterPlan.monthly_price}
                  </span>
                  <span className="text-lg" style={{ color: `${PALETTE.gunmetal}60` }}>
                    /month
                  </span>
                </div>
                <p className="text-sm mt-2" style={{ color: `${PALETTE.gunmetal}70` }}>
                  For freelancers & small marketing teams
                </p>
              </div>

              {/* Features */}
              <ul className="space-y-4 mb-8 flex-1">
                <li className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center mt-0.5"
                    style={{ background: PALETTE.inchworm }}>
                    <Check className="w-3 h-3" style={{ color: PALETTE.gunmetal }} strokeWidth={3} />
                  </div>
                  <span style={{ color: PALETTE.gunmetal }}>
                    <strong>{starterPlan.monthly_video_limit}</strong> video checks per month
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center mt-0.5"
                    style={{ background: PALETTE.inchworm }}>
                    <Check className="w-3 h-3" style={{ color: PALETTE.gunmetal }} strokeWidth={3} />
                  </div>
                  <span style={{ color: PALETTE.gunmetal }}>
                    Unlimited campaigns
                  </span>
                </li>                
                <li className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center mt-0.5"
                    style={{ background: PALETTE.inchworm }}>
                    <Check className="w-3 h-3" style={{ color: PALETTE.gunmetal }} strokeWidth={3} />
                  </div>
                  <span style={{ color: PALETTE.gunmetal }}>
                    Unlimited shareable submission links
                  </span>
                </li>          
              </ul>

              {/* CTA Button */}
              {(!hasActiveSubscription || isTrialing || isTrialEnded) && (
                <Button
                  onClick={() => handleSubscribe('starter')}
                  disabled={processingPlan === 'starter'}
                  className="w-full h-12 rounded-full text-base font-semibold"
                  style={{ 
                    background: PALETTE.inchworm, 
                    color: PALETTE.gunmetal 
                  }}
                >
                  {processingPlan === 'starter' ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Processing...
                    </>
                  ) : (
                    <>
                      Subscribe
                      <ExternalLink className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              )}

              {hasActiveSubscription && subscription?.plan_id === 'starter' && (
                <div className="w-full h-12 rounded-full flex items-center justify-center text-center">
                  <span className="text-sm" style={{ color: `${PALETTE.gunmetal}70` }}>
                    You're currently on this plan ✓
                  </span>
                </div>
              )}

              {/* Downgrade button - shown when user is on Pro plan */}
              {hasActiveSubscription && subscription?.plan_id === 'pro' && (
                <Button
                  onClick={handleManageSubscription}
                  disabled={processingPlan === 'billing'}
                  className="w-full h-12 rounded-full text-base font-semibold"
                  style={{ 
                    background: PALETTE.gunmetal, 
                    color: PALETTE.brightSnow 
                  }}
                >
                  {processingPlan === 'billing' ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Processing...
                    </>
                  ) : (
                    <>
                      Downgrade to this plan
                      <ExternalLink className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Pro Plan Card */}
          <Card className="w-full border-2 hover:border-opacity-100 transition-all duration-300 flex flex-col" 
            style={{ borderColor: PALETTE.inchworm }}>
            <CardContent className="p-8 flex flex-col flex-1">
              {/* Plan Header */}
              <div className="text-center mb-6">
                <div className="flex items-center justify-center gap-2">
                  <h2 className="text-2xl font-bold mb-1" style={{ color: PALETTE.gunmetal }}>
                    {proPlan.name}
                  </h2>
                  <span 
                    className="px-3 py-1 rounded-full text-xs font-semibold"
                    style={{ 
                      background: PALETTE.inchworm,
                      color: PALETTE.gunmetal
                    }}
                  >
                    Best Value
                  </span>
                </div>
              </div>

              {/* Price */}
              <div className="text-center mb-6">
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-5xl font-bold" style={{ color: PALETTE.gunmetal }}>
                    €{proPlan.monthly_price}
                  </span>
                  <span className="text-lg" style={{ color: `${PALETTE.gunmetal}60` }}>
                    /month
                  </span>
                </div>
                <p className="text-sm mt-2" style={{ color: `${PALETTE.gunmetal}70` }}>
                  For growing agencies & mid-sized marketing teams
                </p>
              </div>

              {/* Features */}
              <ul className="space-y-4 mb-8 flex-1">
                <li className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center mt-0.5"
                    style={{ background: PALETTE.inchworm }}>
                    <Check className="w-3 h-3" style={{ color: PALETTE.gunmetal }} strokeWidth={3} />
                  </div>
                  <span style={{ color: PALETTE.gunmetal }}>
                    <strong>{proPlan.monthly_video_limit}</strong> video checks per month
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center mt-0.5"
                    style={{ background: PALETTE.inchworm }}>
                    <Check className="w-3 h-3" style={{ color: PALETTE.gunmetal }} strokeWidth={3} />
                  </div>
                  <span style={{ color: PALETTE.gunmetal }}>
                    Unlimited campaigns
                  </span>
                </li>                
                <li className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center mt-0.5"
                    style={{ background: PALETTE.inchworm }}>
                    <Check className="w-3 h-3" style={{ color: PALETTE.gunmetal }} strokeWidth={3} />
                  </div>
                  <span style={{ color: PALETTE.gunmetal }}>
                    Unlimited shareable submission links
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center mt-0.5"
                    style={{ background: PALETTE.inchworm }}>
                    <Check className="w-3 h-3" style={{ color: PALETTE.gunmetal }} strokeWidth={3} />
                  </div>
                  <span style={{ color: PALETTE.gunmetal }}>
                    Guided onboarding
                  </span>
                </li>             
              </ul>

              {/* CTA Button */}
              {(!hasActiveSubscription || isTrialing || isTrialEnded) && (
                <Button
                  onClick={() => handleSubscribe('pro')}
                  disabled={processingPlan === 'pro'}
                  className="w-full h-12 rounded-full text-base font-semibold"
                  style={{ 
                    background: PALETTE.inchworm, 
                    color: PALETTE.gunmetal 
                  }}
                >
                  {processingPlan === 'pro' ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Processing...
                    </>
                  ) : (
                    <>
                      Subscribe
                      <ExternalLink className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              )}

              {hasActiveSubscription && subscription?.plan_id === 'pro' && (
                <div className="w-full h-12 rounded-full flex items-center justify-center text-center">
                  <span className="text-sm" style={{ color: `${PALETTE.gunmetal}70` }}>
                    You're currently on this plan ✓
                  </span>
                </div>
              )}

              {/* Upgrade button - shown when user is on Starter plan */}
              {hasActiveSubscription && subscription?.plan_id === 'starter' && (
                <Button
                  onClick={handleManageSubscription}
                  disabled={processingPlan === 'billing'}
                  className="w-full h-12 rounded-full text-base font-semibold"
                  style={{ 
                    background: PALETTE.inchworm, 
                    color: PALETTE.gunmetal 
                  }}
                >
                  {processingPlan === 'billing' ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      Processing...
                    </>
                  ) : (
                    <>
                      Upgrade to this plan
                      <ExternalLink className="w-4 h-4 ml-2" />
                    </>
                  )}
                </Button>
              )}
            </CardContent>
          </Card>

          {/* Custom Plan Card */}
          <Card className="w-full border-2 hover:border-opacity-100 transition-all duration-300 flex flex-col" 
            style={{ borderColor: `${PALETTE.americanSilver}60` }}>
            <CardContent className="p-8 flex flex-col flex-1">
              {/* Plan Header */}
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold mb-1" style={{ color: PALETTE.gunmetal }}>
                  Custom
                </h2>                
              </div>

              {/* Price */}
              <div className="text-center mb-6">
                <div className="flex items-baseline justify-center gap-1">
                  <span className="text-5xl font-bold" style={{ color: PALETTE.gunmetal }}>
                    Contact us
                  </span>
                </div>
                <p className="text-sm mt-2" style={{ color: `${PALETTE.gunmetal}70` }}>
                  For large agencies & enterprise brands
                </p>
              </div>

              {/* Features */}
              <ul className="space-y-4 mb-8 flex-1">
                <li className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center mt-0.5"
                    style={{ background: PALETTE.inchworm }}>
                    <Check className="w-3 h-3" style={{ color: PALETTE.gunmetal }} strokeWidth={3} />
                  </div>
                  <span style={{ color: PALETTE.gunmetal }}>
                    Custom built packages
                  </span>
                </li>       
                <li className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center mt-0.5"
                    style={{ background: PALETTE.inchworm }}>
                    <Check className="w-3 h-3" style={{ color: PALETTE.gunmetal }} strokeWidth={3} />
                  </div>
                  <span style={{ color: PALETTE.gunmetal }}>
                    Custom branding
                  </span>
                </li>         
              </ul>

              {/* CTA Button - Always shown for Custom plan */}
              <Button
                onClick={() => window.location.href = 'mailto:info@brandcomply.ai'}
                className="w-full h-12 rounded-full text-base font-semibold"
                style={{ 
                  background: PALETTE.inchworm, 
                  color: PALETTE.gunmetal 
                }}
              >
                Talk to us
                <ExternalLink className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mt-6 p-4 rounded-lg text-center" style={{ background: `${PALETTE.orange}10` }}>
            <p className="text-sm" style={{ color: PALETTE.orange }}>{error}</p>
          </div>
        )}

        {/* Subscription Required Info */}
        {!hasActiveSubscription && !isTrialing && (
          <div className="mt-8 text-center">
            <p className="text-sm" style={{ color: `${PALETTE.gunmetal}60` }}>
              {isTrialEnded ? 'Your trial has ended. Subscribe to continue using BrandComply.' : 'A subscription is required to use BrandComply. Cancel anytime.'}
            </p>
          </div>
        )}

        {/* FAQ Section */}
        <div className="mt-16">
          <h3 className="text-xl font-semibold text-center mb-8" style={{ color: PALETTE.gunmetal }}>
            Frequently Asked Questions
          </h3>
          <div className="space-y-2 max-w-2xl mx-auto">
            {faqItems.map((faq, index) => (
              <Card key={index} className="border" style={{ borderColor: `${PALETTE.americanSilver}30` }}>
                <CardContent className="p-0">
                  <button
                    className="w-full p-4 text-left flex items-center justify-between hover:bg-gray-50 transition-colors"
                    onClick={() => toggleFaqItem(index)}
                  >
                    <h3 className="text-base font-semibold" style={{ color: PALETTE.gunmetal }}>
                      {faq.question}
                    </h3>
                    <ChevronDown
                      className={`w-4 h-4 transition-transform ${openFaqItems[index] ? 'rotate-180' : ''}`}
                      style={{ color: PALETTE.gunmetal }}
                    />
                  </button>
                  {openFaqItems[index] && (
                    <div className="px-4 pb-4">
                      <p className="text-sm" style={{ color: `${PALETTE.gunmetal}70` }}>
                        {faq.answer}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPage;

